import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Modal from '../../../../components/Modal'
import Loader from '../../../../components/Loader'
import Toast from '../../../../components/Toast/Toast'
import { createCourse, updateCourse } from '../../../../services/Courses'
import { TAGS } from '../../../../services/Tags'
import { globalBootstrap } from '../../../..'

export const COURSE_FORM_MODAL_ID = 'course-form-modal'

// Modal de crear/editar curso. Si `course` viene con id → modo edición.
const CourseFormModal = ({ course = null, onClose = () => {} }) => {
  const queryClient = useQueryClient()
  const isEdit = Boolean(course?.id)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [essential, setEssential] = useState(false)
  const [active, setActive] = useState(true)
  const [classUrl, setClassUrl] = useState('')

  // Sincroniza el formulario cuando cambia el curso seleccionado (edición) o se abre en modo crear.
  useEffect(() => {
    setTitle(course?.title || '')
    setDescription(course?.description || '')
    setEssential(Boolean(course?.essential))
    setActive(course?.active ?? true)
    setClassUrl(course?.classUrl || '')
  }, [course])

  const closeModal = () => {
    globalBootstrap.Modal.getOrCreateInstance(`#${COURSE_FORM_MODAL_ID}`).hide()
    onClose()
  }

  const handleSuccess = ({ response, res }, successMsg) => {
    if (!response?.ok) {
      Toast({
        icon: 'error',
        title: res?.msg || res?.error || 'No se pudo guardar el curso',
      })
      return
    }
    queryClient.invalidateQueries([TAGS.courses_list])
    if (isEdit) queryClient.invalidateQueries([TAGS.course_one, course.id])
    Toast({ title: successMsg })
    closeModal()
  }

  const { mutate: create, isLoading: isCreating } = useMutation(createCourse, {
    onSuccess: (data) => handleSuccess(data, 'Curso creado correctamente'),
    onError: () => Toast({ icon: 'error', title: 'No se pudo crear el curso' }),
  })

  const { mutate: update, isLoading: isUpdating } = useMutation(updateCourse, {
    onSuccess: (data) => handleSuccess(data, 'Curso actualizado correctamente'),
    onError: () =>
      Toast({ icon: 'error', title: 'No se pudo actualizar el curso' }),
  })

  const isLoading = isCreating || isUpdating

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!title.trim() || !description.trim()) {
      Toast({ icon: 'error', title: 'Completa título y descripción' })
      return
    }

    if (isEdit) {
      update({
        courseId: course.id,
        title: title.trim(),
        description: description.trim(),
        essential,
        typeCourseId: course?.type_course_id ?? null,
        active,
        classUrl: classUrl.trim(),
      })
    } else {
      create({
        title: title.trim(),
        description: description.trim(),
        essential,
        typeCourseId: null,
        classUrl: classUrl.trim(),
      })
    }
  }

  return (
    <Modal
      modalId={COURSE_FORM_MODAL_ID}
      title={isEdit ? 'Editar curso' : 'Nuevo curso'}
      titleClassName="fw-bolder text-on-bg-primary"
      modalSize="modal-lg"
    >
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label
            className="form-label text-on-bg-primary fw-bold"
            style={{ fontSize: '13px' }}
          >
            Título *
          </label>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Título del curso"
            required
          />
        </div>

        <div className="mb-3">
          <label
            className="form-label text-on-bg-primary fw-bold"
            style={{ fontSize: '13px' }}
          >
            Descripción *
          </label>
          <textarea
            className="form-control"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Descripción del curso"
            required
          />
        </div>

        <div className="mb-3">
          <label
            className="form-label text-on-bg-primary fw-bold"
            style={{ fontSize: '13px' }}
          >
            URL de la clase
          </label>
          <input
            type="text"
            className="form-control"
            value={classUrl}
            onChange={(e) => setClassUrl(e.target.value)}
            placeholder="https://..."
          />
        </div>

        <div className="d-flex flex-column gap-2 mb-4">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="course-essential"
              checked={essential}
              onChange={(e) => setEssential(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="course-essential">
              Curso esencial
            </label>
          </div>
          {isEdit && (
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="course-active"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="course-active">
                Activo
              </label>
            </div>
          )}
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-white border"
            data-bs-dismiss="modal"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-bg-CTA-primary d-flex align-items-center justify-content-center gap-2"
            disabled={isLoading}
            style={{ minWidth: '140px' }}
          >
            {isLoading ? <Loader /> : isEdit ? 'Guardar cambios' : 'Crear curso'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default CourseFormModal
